import {
  CalendarIcon,
  MapPinIcon as LocationMarkerIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/20/solid";
import advancedFormat from "dayjs/plugin/advancedFormat";
import dayjs from "dayjs";
import Link from "next/link";
import React from "react";

import { getAllJobPostings, getSettingsForPage } from "../lib/api";
import { getDepartmentLabel } from "../lib/helpers";
import ImageFeature from "../components/image-feature";
import Meta from "../components/meta";

dayjs.extend(advancedFormat);

const emptyState = (
  <div className="flex flex-col max-w-md text-center mx-auto justify-center items-center">
    <svg
      className="h-12 w-12 text-gray-400"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1}
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
      />
    </svg>

    <div className="relative mx-auto px-4 max-w-7xl sm:px-6 lg:px-8">
      <h3 className="mt-2 text-sm font-medium text-gray-900">
        No Active Career Opportunities
      </h3>

      <p className="mt-1 text-sm text-gray-500">
        We do not have any open career opportunities. Feel free to send your
        resume to{" "}
        <a
          href="mailto:info@clarksullivan.com"
          className="text-accent hover:underline"
        >
          info@clarksullivan.com
        </a>{" "}
        to be kept on file for future openings.
      </p>
    </div>
  </div>
);

function groupJobsByDepartment(jobs) {
  return jobs.reduce((acc, job) => {
    const department = job.department || "Other";
    if (!acc[department]) {
      acc[department] = [];
    }
    acc[department].push(job);
    return acc;
  }, {});
}

export default function Careers({ data, settings }) {
  const hasData = data && data.length;
  const groupedJobs = hasData ? groupJobsByDepartment(data) : {};

  return (
    <div>
      <Meta path="/careers" {...settings.seo} />

      <div className="bg-white relative">
        <div className="bg-gradient-to-b from-accent-50 via-white to-white h-[700px] absolute top-0 left-0 right-0 z-0 pointer-events-none"></div>

        <div className="relative">
          <div className="max-w-7xl mx-auto pt-10 pb-12 px-4 sm:px-6 lg:px-8">
            <div className="pb-12 xl:flex xl:items-center xl:justify-between">
              <div>
                <h1 className="text-3xl sm:text-4xl font-extrabold sm:tracking-tight text-gray-900">
                  {settings.title}
                </h1>
                <p className="mt-5 text-xl text-gray-500">
                  {settings.subtitle}
                </p>
              </div>
            </div>

            <div className="relative">
              {hasData ? (
                <div className="space-y-8">
                  {Object.entries(groupedJobs).map(([department, jobs]) => (
                    <div
                      key={department}
                      className="bg-white border-gray-200 border overflow-hidden rounded-lg"
                    >
                      <div className="bg-gray-50 px-4 sm:px-6 py-3 border-b border-gray-200">
                        <h2 className="text-lg font-medium text-gray-900">
                          {getDepartmentLabel(department)}
                        </h2>
                      </div>
                      <ul role="list" className="divide-y divide-gray-200">
                        {jobs.map((job) => (
                          <li key={job["_id"]}>
                            <Link
                              href={`/careers/${job.slug?.current || job._id}`}
                              className="block hover:bg-gray-50"
                            >
                              <div className="px-4 py-4 sm:px-6">
                                <div className="flex items-center justify-between">
                                  <p className="text-sm font-medium text-accent-600 truncate">
                                    {job.title}
                                  </p>
                                  <div className="ml-2 flex-shrink-0 flex">
                                    <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-accent-100 text-accent-800">
                                      {job.type}
                                    </p>
                                  </div>
                                </div>
                                <div className="mt-2 sm:flex sm:justify-between">
                                  <div className="sm:flex">
                                    <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                                      <LocationMarkerIcon
                                        className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                                        aria-hidden="true"
                                      />
                                      {job.location}
                                    </p>

                                    {job.salaryRange ? (
                                      <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                                        <CurrencyDollarIcon
                                          className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                                          aria-hidden="true"
                                        />
                                        {job.salaryRange}
                                      </p>
                                    ) : null}
                                  </div>

                                  {job.closeDate ? (
                                    <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                                      <CalendarIcon
                                        className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                                        aria-hidden="true"
                                      />
                                      <p>
                                        Closing on{" "}
                                        <time dateTime={job.closeDate}>
                                          {dayjs(job.closeDate).format(
                                            "MMMM Do, YYYY"
                                          )}
                                        </time>
                                      </p>
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ) : (
                emptyState
              )}
            </div>
          </div>
        </div>

        <div className="relative pt-4 pb-32 overflow-hidden space-y-24">
          {settings.features?.map((feature, index) => (
            <ImageFeature key={feature._key} index={index} data={feature} />
          ))}
        </div>

        <div className="bg-accent-700">
          <div className="max-w-7xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-extrabold text-white">
              {settings.faqsTitle}
            </h2>
            <div className="mt-6 border-t border-accent-300 border-opacity-25 pt-10">
              <dl className="space-y-10 md:space-y-0 md:grid md:grid-cols-2 md:grid-rows-2 md:gap-x-8 md:gap-y-12">
                {settings.faqs.map((item) => (
                  <div key={item._key}>
                    <dt className="text-lg leading-6 font-medium text-white">
                      {item.question}
                    </dt>
                    <dd className="mt-2 text-base text-accent-200">
                      {item.answer}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export async function getStaticProps({ preview = false }) {
  const data = await getAllJobPostings(preview);
  const settings = await getSettingsForPage("careers");

  return {
    props: { data, settings },
    revalidate: 10,
  };
}
